/**
 * This exists as a separate approach from get-feature-flags-from-url.ts,
 * as that has been growing large & complex -> splitting out param override concerns
 * in this manner should make it more straightforward to grok, until we see a core need to
 * unify the approaches -
 *
 * i.e. treat this as a step incremental improvement to allow handling overriding
 * multi variant experiments.
 */
import { QUERY_PARAM_EXPERIMENT_PARAMS } from '@canvas/constants/query-params';

import { isAllowedEnvironment } from './is-allowed-environment';

export type ExperimentParamOverrides = {
  [experimentName: string]: {
    [paramName: string]: any;
  };
};

function parseValue(value: string): any {
  if (value.toLowerCase() === 'true') {
    return true;
  }
  if (value.toLowerCase() === 'false') {
    return false;
  }
  const numValue = Number(value);
  if (!isNaN(numValue) && value.trim() !== '') {
    return numValue;
  }
  return value;
}

export const getExperimentParamOverrides = (
  urlString?: string
): ExperimentParamOverrides => {
  if (!isAllowedEnvironment()) {
    return {};
  }

  const overrides: ExperimentParamOverrides = {};

  if (!urlString) {
    return overrides;
  }

  try {
    const url = new URL(urlString);

    const hashFragments = url.hash.split('?');
    const hashParamsString = hashFragments[hashFragments.length - 1] ?? '';

    const processParam = (param: string) => {
      if (!param) {
        return;
      }

      const parts = param.split('.');
      if (parts.length !== 3) {
        console.warn(
          `Invalid experiment param format: "${param}". Expected format: experimentName.paramName.value`
        );
        return;
      }

      const [experimentName, paramName, value] = parts;

      if (!experimentName || !paramName || value === undefined) {
        return;
      }

      if (!overrides[experimentName]) {
        overrides[experimentName] = {};
      }

      overrides[experimentName][paramName] = parseValue(value);
    };

    new URLSearchParams(hashParamsString)
      .getAll(QUERY_PARAM_EXPERIMENT_PARAMS)
      .forEach(processParam);

    url.searchParams?.getAll(QUERY_PARAM_EXPERIMENT_PARAMS)?.forEach(processParam);
  } catch (e) {
    console.error('Error parsing experiment param overrides from URL:', e);
  }

  return overrides;
};
